import { Component, type ErrorInfo, type ReactNode } from "react";
import { Box, Button, Typography } from "@mui/material";

interface Props {
  children: ReactNode;
  // Changing this clears a caught error (keyed to the active session).
  resetKey?: string;
}

interface State {
  hasError: boolean;
}

// Safety net so a transient assistant-ui render error in the chat surface
// degrades to an inline message instead of white-screening the whole app.
export default class ChatErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidUpdate(prev: Props) {
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.warn("ChatErrorBoundary caught:", error.message, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Something went wrong displaying this conversation.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => this.setState({ hasError: false })}
          >
            Reload conversation
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}
